import { useCallback, useEffect, useState } from 'react';
import { api } from '../../shell/bridge';
import { Switch } from '../../ui/Switch';
import { SkeletonBlock } from '../../ui/Skeleton';
import './ai.css';

type Category = 'chat' | 'inbox' | 'questions' | 'vault' | 'todos';

const CATEGORIES: { key: Category; label: string; note: string }[] = [
  { key: 'chat', label: 'Chat', note: 'Let the agent read your shared thread' },
  { key: 'inbox', label: 'Inbox', note: 'Let the agent read your couple inbox' },
  { key: 'questions', label: 'Questions', note: 'Let the agent read daily answers after reveal' },
  { key: 'vault', label: 'Vault', note: 'Let the agent read saved links' },
  { key: 'todos', label: 'To-dos', note: 'Let the agent manage shared to-dos' },
];

interface LogEntry {
  id: string;
  category: Category;
  allowed: boolean;
  toggleState: boolean;
  reason: string;
  at: number;
}

/**
 * AI access panel — per-category toggles (all off by default) plus the recent
 * access log, ported from `ai/ui.ts`.
 *
 * Same endpoints and same PUT semantics; the markup is JSX now, so there is no
 * `innerHTML` rebuild per toggle and no button element recreated under the
 * user's finger mid-click.
 */
export function AIPanel() {
  const [toggles, setToggles] = useState<Record<Category, boolean> | null>(null);
  const [entries, setEntries] = useState<LogEntry[] | null>(null);
  const [busyKey, setBusyKey] = useState<Category | null>(null);
  const [failed, setFailed] = useState(false);

  const loadLog = useCallback(async () => {
    try {
      const data = await api<{ entries: LogEntry[] }>('/api/ai/log?limit=10');
      setEntries(data.entries ?? []);
    } catch (err) {
      console.error('[Pear] ai log failed', err);
      setEntries(prev => prev ?? []);
    }
  }, []);

  const loadToggles = useCallback(async () => {
    try {
      const data = await api<{ toggles: Record<Category, boolean> }>('/api/ai/toggles');
      setToggles(data.toggles);
      setFailed(false);
      return data.toggles;
    } catch (err) {
      console.error('[Pear] toggles load failed', err);
      setFailed(true);
      return null;
    }
  }, []);

  useEffect(() => {
    void loadToggles().then(current => {
      if (current) void loadLog();
    });
  }, [loadToggles, loadLog]);

  const toggle = async (cat: Category, on: boolean) => {
    if (busyKey) return;
    setBusyKey(cat);
    try {
      const data = await api<{ toggles: Record<Category, boolean> }>('/api/ai/toggles', {
        method: 'PUT',
        body: { category: cat, on: !on },
      });
      setToggles(data.toggles);
      await loadLog();
    } catch (err) {
      console.error('[Pear] toggle failed', err);
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div className="ai-panel">
      <h2 className="ai-title">AI access</h2>
      <p className="ai-lead">
        Everything starts off. The agent can only read a category you switch on here — and every read is logged.
      </p>

      {toggles === null && !failed && (
        <div className="ai-list" aria-busy="true">
          <SkeletonBlock lines={3} />
        </div>
      )}

      {failed && (
        <p className="ai-error">Couldn’t load your toggles. Pull to retry or check back shortly.</p>
      )}

      {toggles && (
        <div className="ai-group">
          <div className="ai-group-head">
            <span className="ai-group-title">What the agent may read</span>
            <span className="ai-group-count">
              {Object.values(toggles).filter(Boolean).length} of {CATEGORIES.length} on
            </span>
          </div>
          <div className="ai-list">
            {CATEGORIES.map(cat => {
              const on = toggles[cat.key] === true;
              const busy = busyKey === cat.key;
              return (
                <div className="ai-row" key={cat.key}>
                  <div className="ai-text">
                    <div className="ai-label">{cat.label}</div>
                    <div className="ai-note">{cat.note}</div>
                  </div>
                  <Switch
                    checked={on}
                    busy={busy}
                    label={`${cat.label} access`}
                    disabled={busyKey !== null}
                    onChange={() => void toggle(cat.key, on)}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* The two-path agent control moved to the Settings screen
          (card 0xhNPdS42FTh): permissions stay here, behaviour control lives
          with the rest of the settings. */}

      <div className="ai-log">
        <h3 className="ai-log-title">Recent reads</h3>
        {entries === null && <SkeletonBlock lines={2} />}
        {entries !== null && entries.length === 0 && <p className="ai-log-empty">No agent reads yet.</p>}
        {entries?.map(e => (
          <div className="ai-log-row" key={e.id}>
            <span>
              {e.allowed ? 'allowed' : 'denied'} · {e.category} — {e.reason}
            </span>
            <span className="ai-log-time">{new Date(e.at).toLocaleTimeString()}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
